<template>
  <div class="favourite-add-form" :class="{ desktop: $isDesktop }">
    <header>
      <translate v-if="isAddedToFavourites" tag="h3" :tt="{ campsiteName }">
        Change {campsiteName} in favourites
      </translate>
      <translate v-else tag="h3" :tt="{ campsiteName }">
        Add {campsiteName} to favourites
      </translate>
    </header>
    <template v-if="state.matches('showingMainView')">
      <div v-if="!store.currentlyLoggedIn" class="favourite-list my-favourites">
        <img
          v-if="store.favouritesPrimaryPhoto"
          :src="favouritesImageUrl"
          class="img"
        />
        <img v-else src="~/assets/images/photo-icon.svg" class="img" />
        <base-checkbox
          name="default-favourite"
          :model-value="state.hasTag('showingDefaultFavourite')"
          @change="handleDefaultFavouriteSwitch"
        >
          <span
            ><translate>My favourites</translate
            ><translate :tt="{ count: defaultFavouritesCount }" class="count"
              >{count} sites</translate
            ></span
          >
        </base-checkbox>
      </div>

      <!-- <input
            placeholder="Search for list"
            @input="filterWishlists"
          > -->
      <favourite-add-form-wishlist
        v-for="list in wishlists"
        :key="list.id"
        :wishlist-machine="list"
      />

      <template v-if="!!Object.keys(guestWishlists).length">
        <favourite-add-form-wishlist
          v-for="list in guestWishlists"
          :key="list.id"
          :wishlist-machine="list"
        />
      </template>

      <pu-modal-no-hash
        v-if="store.currentlyLoggedIn"
        :use-portal="false"
        class="add-new-favourites-list-modal"
        modal-class="add-new-favourites-list-modal"
        :class="{ desktop: $isDesktop }"
        :show-modal="showAddFormModal"
        @change-show-modal-state="addToNewWishlistFormVisibilityChanged"
      >
        <template #modal-trigger>
          <button class="create-new-list">
            <img
              src="~/assets/images/plus-filled.svg"
              width="32"
              height="32"
              alt=""
            />
            <translate>Create a new list</translate>
          </button>
        </template>
        <template #default="{ closeModal }">
          <wishlist-form-inputs
            :errors="context?.errors"
            @saveToList="saveToNewList($event, closeModal)"
          />
        </template>
      </pu-modal-no-hash>
    </template>
    <div v-if="state.matches('initial')" class="loading-spinner">
      <loading-spinner />
    </div>
    <div v-if="!store.currentlyLoggedIn" class="logged-out-note">
      <login-modal :use-modal-portal="false">
        <i18n-t
          tag="p"
          keypath="{logIn} to create and share custom lists and access your favourites from anywhere."
        >
          <template #logIn>
            <translate tag="a" @click.prevent=""> Log in </translate>
          </template>
        </i18n-t>
      </login-modal>
      <i18n-t
        tag="p"
        class="create-account"
        keypath="If you don't already have one you can {createAnAccountHere}."
      >
        <template #createAnAccountHere>
          <nuxt-link :to="$puLocalePath({ path: '/accounts/signup/' })">
            <translate>create an account here</translate>
          </nuxt-link>
        </template>
      </i18n-t>
    </div>
    <div
      v-observe-visibility="{
        callback: headerVisibilityChanged,
        throttle: 200,
        throttleOptions: {
          leading: 'both',
        },
        intersection: {
          threshold: 1,
        },
      }"
      class="footer-observer"
    />
    <footer :class="[{ pinned: !footerIsVisible }]">
      <base-button
        css-class="primary"
        class="done-btn"
        @click="triggerDoneProcess"
      >
        <translate>Done</translate>
      </base-button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { interpret } from 'xstate-v4'
import { getUrlPrefix } from '~/lang/locales'
import { useFavouritesStore } from '~/stores/useFavouritesStore'
import { logger } from '../../../../utility/logger'
import {
  FavouriteCampsiteMachineContext,
  FavouriteMachineService,
  createFavouriteMachine,
} from '../../machines/favourite.machine'

const i18n = useI18n()
const { $isDesktop } = useNuxtApp()

const lang = (useRoute().params.lang as string) || 'en-gb'
const store = useFavouritesStore(usePinia())
const onboardingStore = useOnboardingStore(usePinia())
const footerIsVisible = ref(true)
const snackbar = process.client ? useSnackbar() : null

const props = defineProps<{
  campsiteId: string
  campsiteName: string
}>()

const emit = defineEmits(['process-done'])

const favouritesMachine = createFavouriteMachine({
  campsiteId: props.campsiteId,
  favStore: store,
})
const state = ref<FavouriteMachineService['state']>(
  favouritesMachine.initialState as unknown as FavouriteMachineService['state'],
)
const context = ref<FavouriteCampsiteMachineContext | null>(null)

const defaultFavouritesCount = computed(() => store.count)

const showAddFormModal = ref<boolean>(false)

const isAddedToFavourites = computed(() => !!context.value?.favouriteDetails)

const machine = interpret(favouritesMachine).onTransition((s) => {
  state.value = s as unknown as FavouriteMachineService['state']
  context.value = s.context as FavouriteCampsiteMachineContext | null
})

const wishlists = computed(() => {
  if (!context.value) {
    return []
  }
  const wishlistContext = context.value
  if (!wishlistContext) {
    return []
  }
  const wishlists = !wishlistContext.filteredWishlists
    ? wishlistContext.wishlists
    : wishlistContext.filteredWishlists
  return Object.values(wishlists)
    .filter((wishlist) => wishlist.state.context.isOwner)
    .sort(
      (a, b) =>
        (b.state.context.isMain ? 1 : 0) - (a.state.context.isMain ? 1 : 0),
    )
})

const guestWishlists = computed(() => {
  const wishlistContext = context.value
  if (!wishlistContext) {
    return []
  }
  const wishlists = !wishlistContext.filteredWishlists
    ? wishlistContext.wishlists
    : wishlistContext.filteredWishlists
  return Object.values(wishlists).filter(
    (wishlist) => !wishlist.state.context.isOwner,
  )
})

onMounted(async () => {
  await store.getAllGuestWishlists()
  machine.start()
})

watch(
  () => store.currentlyLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn) {
      machine.send({ type: 'REFETCH_WISHLISTS' })
    }
  },
)

function addToNewWishlistFormVisibilityChanged(event) {
  if (event) {
    machine.send('SHOW_ADD_TO_NEW_WISHLIST_FORM')
  }
}

function handleDefaultFavouriteSwitch() {
  if (state.value.hasTag('showingDefaultFavourite')) {
    machine.send('REMOVE_FROM_FAVOURITES')
  } else {
    machine.send('ADD_TO_FAVOURITES')
  }
}

function triggerDoneProcess() {
  const changes = context.value?.changes || {}
  const realChanges = Object.entries(changes).filter(
    ([_, value]) => value.initial !== value.current,
  )

  if (realChanges.length > 0) {
    const addedLists = realChanges
      .filter(([_, value]) => value.current)
      .map(([key, _]) => getWishlistName(key))
    const removedLists = realChanges
      .filter(([_, value]) => !value.current)
      .map(([key, _]) => getWishlistName(key))

    let message = ''

    if (addedLists.length > 0 && removedLists.length > 0) {
      message = formatCombinedChangeMessage(
        props.campsiteName,
        addedLists,
        removedLists,
      )
    } else if (addedLists.length > 0) {
      message = formatChangeMessage(props.campsiteName, addedLists, true)
    } else if (removedLists.length > 0) {
      message = formatChangeMessage(props.campsiteName, removedLists, false)
    }
    message =
      message +
      ` <a href="${getUrlPrefix(lang)}/favourites/">` +
      i18n.t('View favourites') +
      '</a>'
    showInfoMessage(message)
    if (addedLists.length > 0) {
      onboardingStore.setFavouritesOnboardingState({ closed: false })
    }
    machine.send('RESET_CHANGES')
  }
  emit('process-done', true)
}

function formatChangeMessage(
  campsiteName: string,
  lists: string[],
  isAdding: boolean,
): string {
  if (lists.length === 1) {
    return isAdding
      ? i18n.t('{campsiteName} has been added to {list}.', {
          campsiteName,
          list: lists[0],
        })
      : i18n.t('{campsiteName} has been removed from {list}.', {
          campsiteName,
          list: lists[0],
        })
  } else if (lists.length === 2) {
    return isAdding
      ? i18n.t('{campsiteName} has been added to {list1} and {list2}.', {
          campsiteName,
          list1: lists[0],
          list2: lists[1],
        })
      : i18n.t('{campsiteName} has been removed from {list1} and {list2}.', {
          campsiteName,
          list1: lists[0],
          list2: lists[1],
        })
  } else {
    return isAdding
      ? i18n.t(
          '{campsiteName} has been added to {list1}, {list2} and {count} more.',
          {
            campsiteName,
            list1: lists[0],
            list2: lists[1],
            count: lists.length - 2,
          },
        )
      : i18n.t(
          '{campsiteName} has been removed from {list1}, {list2} and {count} more.',
          {
            campsiteName,
            list1: lists[0],
            list2: lists[1],
            count: lists.length - 2,
          },
        )
  }
}

function formatCombinedChangeMessage(
  campsiteName: string,
  addedLists: string[],
  removedLists: string[],
): string {
  if (addedLists.length === 1 && removedLists.length === 1) {
    return i18n.t(
      '{campsiteName} has been added to {addedList}, and removed from {removedList}.',
      {
        campsiteName,
        addedList: addedLists[0],
        removedList: removedLists[0],
      },
    )
  } else if (addedLists.length > 1 && removedLists.length === 1) {
    return i18n.t(
      '{campsiteName} has been added to {addedList} and {countAdded} more, and removed from {removedList}.',
      {
        campsiteName,
        addedList: addedLists[0],
        countAdded: addedLists.length - 1,
        removedList: removedLists[0],
      },
    )
  } else if (addedLists.length === 1 && removedLists.length > 1) {
    return i18n.t(
      '{campsiteName} has been added to {addedList}, and removed from {removedList} and {countRemoved} more.',
      {
        campsiteName,
        addedList: addedLists[0],
        removedList: removedLists[0],
        countRemoved: removedLists.length - 1,
      },
    )
  } else {
    return i18n.t(
      '{campsiteName} has been added to {addedList} and {countAdded} more, and removed from {removedList} and {countRemoved} more.',
      {
        campsiteName,
        addedList: addedLists[0],
        countAdded: addedLists.length - 1,
        removedList: removedLists[0],
        countRemoved: removedLists.length - 1,
      },
    )
  }
}

function getWishlistName(key: string | number): string {
  if (key === 'my-favourites') {
    return i18n.t('your favourites')
  }
  const wishlist = store.wishlists[key]
  return wishlist ? wishlist.name : i18n.t('Unknown list')
}

const favouritesImageUrl = computed(() =>
  store.favouritesPrimaryPhoto
    ? `/_cfi/cdn-cgi/image/f=auto,fit=cover,g=auto,quality=70,w=60,h=60${store.favouritesPrimaryPhoto.url}`
    : '~/assets/images/photo-icon.svg',
)

function saveToNewList(event, closeModal) {
  logger('saveToNewList', event)
  machine.send('SAVE_TO_NEW_WISHLIST', { ...event, callback: closeModal })
}

function showInfoMessage(message) {
  snackbar?.add({ text: message, type: 'success' })
}

function headerVisibilityChanged(isVisible: boolean) {
  footerIsVisible.value = isVisible
}
</script>

<style scoped>
.favourite-add-form {
  padding: 1em 1em 1.5em 1em;
  width: 100%;
}

.create-new-list {
  box-shadow: none;
  border: none;
  font-size: var(--body);
  border-radius: 0.5em;
  padding: 0.5em;
  display: flex;
  align-items: center;
  column-gap: 0.7em;
  width: 100%;
  border: 1px solid var(--colour-page-background);
}

.create-new-list:active {
  transform: none;
}

.create-new-list:active img {
  filter: saturate(0.25);
}

.logged-out-note {
  border-top: 1px solid var(--colour-page-background);
  padding: 1em 0 0;
  margin: 1em 0 0;
}

.logged-out-note :deep(.modal-trigger-wrapper) {
  padding: 0;
  background: transparent;
  border-radius: 0;
  display: block;
}

.favourite-add-form :deep(.reset-password .modal-container) {
  width: 94vw;
  right: 3vw;
  left: 3vw;
}

p {
  font-size: var(--body);
  margin: 0 0 0.4em 0;
}

header:has(h3) {
  margin-right: 1.85em;
}

header h3 {
  line-height: 1.1;
}

.favourite-list {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 0.75em;
  margin: 0 0 1em 0;
}

.favourite-list img {
  overflow: hidden;
  border-radius: 0.4em;
}

.favourite-list :deep(.base-checkbox) {
  width: 100%;
}

.favourite-list :deep(.base-checkbox label) {
  flex-direction: row-reverse;
  justify-content: space-between;
  margin: 0;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.favourite-list .count {
  color: var(--colour-text-light);
  display: block;
}

.favourite-list.my-favourites {
  margin: 2em 0 1em 0;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1em;
}

footer {
  position: sticky;
  bottom: 0;
  padding: 1em;
  background: white;
  margin: auto -1em -1em;
}

footer.pinned {
  box-shadow: 0 -0.3em 0.5em rgba(0, 0, 0, 0.2);
}

.footer-observer {
  height: 2em;
  margin-top: 2em;
}

/* desktop & tablet */
.desktop.favourite-add-form {
  padding: 1.4em;
}

.desktop.favourite-add-form footer {
  margin: auto -1.4em -1em;
}

.desktop .logged-out-note :deep(.modal-trigger-wrapper:hover) {
  background: transparent;
}

.desktop p,
.desktop .favourite-list :deep(label),
.desktop.favourite-add-form :deep(.favourite-add-form-wishlist label),
.desktop .create-new-list {
  font-size: initial;
}

.desktop .create-new-list:hover {
  background: #ddd;
}

.favourite-add-form :deep(.favourite-add-form-wishlist:last-of-type) {
  margin-bottom: 1.75em;
}

.favourite-add-form :deep(.favourite-list .img) {
  width: 2.2em;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
}

.favourite-add-form :deep(.favourite-add-form-wishlist:nth-of-type(1n) .img) {
  background-color: var(--colour-light-green);
}

.favourite-add-form :deep(.favourite-add-form-wishlist:nth-of-type(2n) .img) {
  background-color: var(--colour-form-blue);
}

.favourite-add-form :deep(.favourite-add-form-wishlist:nth-of-type(3n) .img) {
  background-color: var(--colour-offers);
}

.favourite-add-form :deep(.favourite-add-form-wishlist:nth-of-type(4n) .img) {
  background-color: var(--colour-orange);
}
</style>
